// V2 card-terminal payment session lifecycle.
//
// Wraps three endpoints rooted at
// `/v2/.../receipts/{id}/terminal-payments`:
//
//   POST                    → start a session against a physical terminal
//   GET    /{session_id}    → poll the session until it reaches a final state
//   DELETE /{session_id}    → best-effort cancel (race-safe; may return
//                              `approved` if the customer beat us to it)
//
// The backend owns the polling loop against the processor (Moneris Go,
// PMT3, etc.) — the FE never talks to a processor directly. Our job here
// is to start the session, keep polling for `display_message` updates,
// and resolve to one of the terminal states (approved / declined /
// cancelled / timeout / error). See V2-API.md §"Terminal payments".
//
// Why a session model: card terminals take up to 5 minutes for a
// customer to insert/tap and approve, and a single long-lived HTTP
// request that wraps that wait is fragile (proxy timeouts, page
// reloads, deploys). The backend stamps a durable session row, polls
// the processor itself, and lets us reattach via session_id after any
// transient client-side blip.

import { useMutation, useQuery, type UseQueryResult } from '@tanstack/react-query'
import { v2, v2api, type V2Envelope } from './v2'
import type { CartPreviewRequest } from './cart'
import type { Receipt } from './receipts'
import { useTerminalPollWatchdog } from '@/lib/terminalPollWatchdog'

export type TerminalPaymentStatus =
  | 'pending'
  | 'approved'
  | 'declined'
  | 'cancelled'
  | 'timeout'
  | 'error'

export interface TerminalPaymentSession {
  session_id: string
  status: TerminalPaymentStatus
  /** Human-facing prompt — "Insert card", "Approving…", "Printing receipts". */
  display_message: string | null
  /** Which backend driver is handling this session. Informational only. */
  processor: 'moneris_go' | 'pmt3' | 'mcore' | 'fake' | string
  amount?: string
  started_at?: string
  completed_at?: string | null
  /** Present only on `approved` — full canonical receipt payload. Same
   *  shape as `/cart/commit` returns, so we can print directly off it. */
  receipt?: Receipt
  /** Present only on `approved` — id of the JT recorded against the
   *  receipt for this session's charge. */
  journal_transaction_id?: number
  /** Present on `declined` / `error` / `timeout`. */
  error_code?: string
  error_message?: string
}

export interface StartTerminalPaymentBody {
  /** Signed dollars. Negative = refund. Backend caps at receipt's outstanding.
   *
   *  Optional when ``items`` is also sent: the BE truncates+replaces the
   *  cart from ``items``, recomputes the total, and uses that as the
   *  charge amount. Send an explicit ``amount`` only for split-tender
   *  (charge less than the recomputed balance on this method). */
  amount?: number
  /** UUID. Reuse to recover a started-but-not-acknowledged session. */
  idempotency_key: string
  /** Current local cart contents. When present, the BE truncate-and-replaces
   *  the receipt's cart from this list BEFORE the terminal touches the
   *  card -- the FE is the source of truth for "what items, how many"
   *  and the BE is the source of truth for "what total". Eliminates the
   *  mid-payment add/remove/swap drift class: a payment never charges
   *  against a cart different from what the cashier currently sees. Same
   *  item shape as /cart/preview + /cart/commit so cartToRequest(cart)
   *  produces a compatible list. */
  items?: CartPreviewRequest['items']
  /** FE-displayed total — backend rejects with `price_drift` if it disagrees.
   *  Redundant when ``items`` is sent (the recommit makes the BE total
   *  authoritative); keep it on legacy callsites for defense in depth. */
  expected_total?: number
  /** Deprecated for the terminal-payment endpoint: card overpayment is
   *  now hard-blocked at the BE regardless of this flag. Field kept for
   *  API compat; BE silently ignores it on `/terminal-payments`. */
  confirm_overpayment?: boolean
  /** Optional override; backend infers from register config when absent. */
  payment_type_hint?: number
  notes?: string
  return_reason_id?: number | null
  return_reason_text?: string | null
}

const POLL_INTERVAL_MS = 500

/**
 * POST /v2/.../receipts/{id}/terminal-payments
 *
 * Starts a card-terminal session. Returns the initial session row
 * (status=`pending`, display_message="Insert card" or similar). Reuse
 * the same `idempotency_key` to recover the cached session after a
 * network blip — backend keys on (receipt_id, idempotency_key) and
 * never starts a second physical-terminal transaction.
 */
export function useStartTerminalPayment() {
  return useMutation({
    mutationFn: async ({
      receiptId,
      body,
    }: {
      receiptId: number
      body: StartTerminalPaymentBody
    }): Promise<TerminalPaymentSession> => {
      const wire: Record<string, unknown> = {
        idempotency_key: body.idempotency_key,
      }
      // Omit amount when items[] is sent without an explicit amount: BE
      // treats absent amount as "charge the full recomputed balance".
      // Including a stale FE amount would either match (no-op) or force
      // the BE through its overpayment gate against the recomputed total.
      if (body.amount !== undefined) {
        wire.amount = body.amount.toFixed(2)
      }
      if (body.items !== undefined) {
        wire.items = body.items
      }
      if (body.expected_total !== undefined) {
        wire.expected_total = body.expected_total.toFixed(2)
      }
      if (body.confirm_overpayment !== undefined) {
        wire.confirm_overpayment = body.confirm_overpayment
      }
      if (body.payment_type_hint !== undefined) {
        wire.payment_type_hint = body.payment_type_hint
      }
      if (body.notes !== undefined) wire.notes = body.notes
      if (body.return_reason_id !== undefined) {
        wire.return_reason_id = body.return_reason_id
      }
      if (body.return_reason_text !== undefined) {
        wire.return_reason_text = body.return_reason_text
      }

      const envelope = await v2api.post<V2Envelope<TerminalPaymentSession>>(
        v2(`/receipts/${receiptId}/terminal-payments`),
        wire,
      )
      return envelope.data
    },
    // LAN-bound terminal payment: don't gate on the browser offline flag.
    networkMode: 'always',
  })
}

/**
 * GET /v2/.../receipts/{id}/terminal-payments/{session_id}
 *
 * Polls the running session every 500ms while pending; auto-stops the
 * moment the backend reports a final status (approved / declined /
 * cancelled / timeout / error). The `display_message` is updated by
 * the backend driver as the customer progresses through the terminal
 * (insert, PIN, approve, etc.) — re-render off `data?.display_message`
 * to mirror the prompt to the cashier.
 *
 * `staleTime: 0` so React Query doesn't dedupe a "ready" response with
 * the previous "pending" — every tick is a fresh truth.
 */
export function useTerminalPaymentSession(
  receiptId: number | null,
  sessionId: string | null,
): UseQueryResult<TerminalPaymentSession> {
  // Capture-only diagnostic sensor (no behavior change): beacons if this
  // poll stalls while the session is still pending. See terminalPollWatchdog.
  useTerminalPollWatchdog(receiptId, sessionId)
  return useQuery({
    queryKey: ['terminalPayment', receiptId, sessionId],
    queryFn: async (): Promise<TerminalPaymentSession> => {
      const envelope = await v2api.get<V2Envelope<TerminalPaymentSession>>(
        v2(`/receipts/${receiptId}/terminal-payments/${sessionId}`),
      )
      return envelope.data
    },
    enabled: receiptId !== null && sessionId !== null,
    refetchInterval: (query) => {
      const data = query.state.data
      if (!data) return POLL_INTERVAL_MS
      return data.status === 'pending' ? POLL_INTERVAL_MS : false
    },
    refetchIntervalInBackground: true,
    staleTime: 0,
    gcTime: 0,
    // LAN-bound, life-critical poll: never let React Query pause it on the
    // (unreliable) browser offline flag. The watchdog also force-onlines on
    // stall, but this stops the pause class at the source.
    networkMode: 'always',
  })
}

/**
 * DELETE /v2/.../receipts/{id}/terminal-payments/{session_id}[?force_dispatch=1]
 *
 * Best-effort cancel. Three race outcomes:
 *
 *  1. Customer hasn't approved yet  → status flips to `cancelled`,
 *     no JT recorded.
 *  2. Customer is in the approved-voidable window (Moneris 5207) →
 *     backend sends an explicit Void, status flips to `cancelled`.
 *  3. Customer already approved (race with our cancel) → response
 *     is `status: approved` and the receipt is paid. Caller must
 *     surface "Already approved — print?" rather than treating it as
 *     a cancellation.
 *
 * `forceDispatch` (default false): opt into the BE's
 * schedule_dispatch path. The FE asserts "I think this session is
 * orphan, please re-dispatch a worker"; the BE independently
 * verifies via the should_force_dispatch liveness guard
 * (ops_terminal_payment.py) and refuses if updated_at indicates
 * the original driver is still heartbeating. This split means the
 * FE is safe to set the flag whenever the cashier explicitly opts
 * into orphan recovery (e.g. handleCancelBusyAndRetry after
 * register_busy) without needing FE-side proof of orphan-ness --
 * the BE won't honor it on a live session.
 *
 * When refused, the cancel still works via the slower path: the
 * running driver picks up cancel_requested on its next poll
 * iteration (~1s for the Moneris Go driver). The FE's polling for
 * status transition succeeds either way.
 *
 * Callers SHOULD leave forceDispatch false for the live in-flight
 * cancel path (handleCancelTerminal -- cashier hit "Cancel
 * Payment" mid-flow). The BE guard would refuse it anyway, but
 * keeping the flag off makes intent explicit: live cancels never
 * try to race their own driver.
 */
export function useCancelTerminalPayment() {
  return useMutation({
    mutationFn: async ({
      receiptId,
      sessionId,
      forceDispatch = false,
    }: {
      receiptId: number
      sessionId: string
      forceDispatch?: boolean
    }): Promise<TerminalPaymentSession> => {
      // Encode forceDispatch as a query string param. The BE handler
      // reads via get_arg_bool('force_dispatch'); '1' / 'true' / 'yes'
      // all coerce true on the BE side.
      const url = forceDispatch
        ? v2(`/receipts/${receiptId}/terminal-payments/${sessionId}?force_dispatch=1`)
        : v2(`/receipts/${receiptId}/terminal-payments/${sessionId}`)
      const envelope = await v2api.delete<V2Envelope<TerminalPaymentSession>>(url)
      return envelope.data
    },
    // LAN-bound terminal payment: don't gate on the browser offline flag.
    networkMode: 'always',
  })
}
